import { createEffect, createSignal } from 'solid-js';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';

export const CautionPhoneInput = (props: {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  onInput?: (e: { target: { value: string } }) => void;
  required?: boolean;
  caution?: boolean;
  error?: string | string[] | null | boolean;
  dataTestId?: string;
  resetDirty?: boolean;
}) => {
  const [isDirty, setIsDirty] = createSignal(false);

  const yellowify = () => {
    return props.caution && isDirty();
  };

  createEffect(() => {
    if (props.resetDirty) {
      setIsDirty(false);
    }
  });

  const hasError = () => {
    return (
      props.error !== false &&
      props.error !== null &&
      props.error !== undefined &&
      (Array.isArray(props.error) ? props.error.length > 0 : props.error !== '')
    );
  };

  const getErrorMessage = () => {
    if (!hasError()) return '';
    return Array.isArray(props.error) ? props.error[0] : props.error;
  };

  const formattedValue = () => {
    if (props.value !== undefined && props.value !== null) {
      return formatPhoneNumber(props.value);
    }
    return '';
  };

  const handleInputChange = (event: Event) => {
    props.onInput?.({
      target: { value: (event.target as HTMLInputElement).value },
    });

    const target = event.target as HTMLInputElement;
    const cursorPosition = target.selectionStart ?? 0;
    const oldValue = target.value;

    // Extract only digits from the entire value
    const newUnformattedValue = oldValue.replace(/[^0-9]/g, '');

    // Block non-numeric characters (letters, special chars, etc.)
    // If the cleaned value is different after removing formatting chars, non-numeric chars were entered
    if (oldValue.replace(/[\s()-]/g, '') !== newUnformattedValue) {
      event.preventDefault();
      // Restore the previous formatted value
      target.value = formattedValue();
      return;
    }

    // Block input if exceeds 10 digits
    if (newUnformattedValue.length > 10) {
      event.preventDefault();
      // Restore the previous formatted value
      target.value = formattedValue();
      return;
    }

    // Get the number of digits before the cursor in the old formatted value
    const digitsBeforeCursor = oldValue
      .substring(0, cursorPosition)
      .replace(/[^0-9]/g, '').length;

    // Mark as dirty if not empty
    const isEmpty = newUnformattedValue === '';
    setIsDirty(!isEmpty);

    // Format the phone number
    const newValue = formatPhoneNumber(newUnformattedValue);

    // Update the value through props
    props.onChange?.(newUnformattedValue);

    // Calculate new cursor position by counting formatting characters
    let newCursorPosition = 0;
    let digitCount = 0;

    // Find position after the same number of digits
    for (let i = 0; i < newValue.length; i++) {
      if (/[0-9]/.test(newValue[i])) {
        digitCount++;
      }
      if (digitCount > digitsBeforeCursor) break;
      newCursorPosition = i + 1;
    }

    // Set the cursor to the right position
    setTimeout(() => {
      target.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <div class="relative my-1">
      <input
        class={`peer w-full px-3 py-2 border rounded-md shadow-xs focus:outline-hidden 
           disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-500 placeholder:opacity-0 focus:placeholder:opacity-100 ${
             hasError()
               ? 'border-error-red focus:border-error-red hover:border-error-red bg-error-red '
               : yellowify()
                 ? 'border-[#f7b355] bg-[#fff8e1]'
                 : 'border-[#80b6cf] focus:border-[#123b50] hover:border-[#123b50]'
           }`}
        value={formattedValue()}
        onInput={handleInputChange}
        onBlur={props.onBlur}
        placeholder={props.placeholder ?? ''}
        disabled={props.disabled}
        data-testid={props.dataTestId}
      />
      <label
        class={`text-shadow-thick absolute bg-transparent left-2 px-1 text-sm font-medium transition-all duration-200 ease-out pointer-events-none -top-2.5
           peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-gray-500 peer-focus:-top-2.5 peer-focus:text-sm peer-disabled:text-gray-400 ${
             hasError()
               ? 'text-error-red peer-focus:text-error-red peer-placeholder-shown:text-error-red'
               : yellowify()
                 ? 'text-caution-yellow'
                 : 'text-armstrong-primary peer-focus:text-armstrong-primary'
           }`}
      >
        {props.label}
        {props.required && <span class="text-error-red bg-white"> *</span>}
      </label>
      {yellowify() && !hasError() && (
        <div class="text-caution-yellow text-xs mt-1 ml-1">
          This will be used when mobile tracking is enabled
        </div>
      )}
      {hasError() && (
        <div class="text-error-red text-xs mt-1 ml-1">{getErrorMessage()}</div>
      )}
    </div>
  );
};
